import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  MenuItem,
  Popover,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Autocomplete from '@mui/material/Autocomplete';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '../../Styles/AccountingStyles.css'
import { useDispatch } from 'react-redux';
import { GetInvestorStatement } from '../../Redux/Actions/strategyService';

const InvestorStatementsTab = ({ fund, investors = [], fundId, firmId }) => {
  // State for investor statements
  const [selectedInvestor, setSelectedInvestor] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [statementData, setStatementData] = useState(null);
  const dropdownRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Months for selection
  const months = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  const availableYears = [2018, 2019, 2020, 2021, 2022, 2023, 2024,2025];

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setShowDateDropdown(false);
      } else if (event.key === 'Enter' && showDateDropdown) {
        setShowDateDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDateDropdown]);

  // Change year
  const changeYear = (direction) => {
    const currentIndex = availableYears.indexOf(currentYear);
    if (direction === 'next' && currentIndex < availableYears.length - 1) {
      setCurrentYear(availableYears[currentIndex + 1]);
    } else if (direction === 'prev' && currentIndex > 0) {
      setCurrentYear(availableYears[currentIndex - 1]);
    }
  };

  const handleDateClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setAnchorEl(null);
  };

  const handleMonthSelect = (month) => {
    setSelectedDate(`${month}/${currentYear}`);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Deduplicate investors by InvestorID or base name/number
  const uniqueInvestors = React.useMemo(() => {
    const seen = new Set();
    return investors.filter(inv => {
      // Remove _SP, _SP2, etc. for uniqueness
      const baseNumber = inv.InvestorNumber ? inv.InvestorNumber.split('_')[0] : inv.InvestorNumber;
      const key = `${inv.InvestorName} (${baseNumber})`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [investors]);

  // Generate statement (API)
  const generateStatement = async () => {
    if (!selectedInvestor || !selectedDate) {
      // Show error or notification
      return;
    }
    setLoading(true);
    setStatementData(null);
    // Parse month and year
    const [month, year] = selectedDate.split('/');
    try {
      const response = await dispatch(GetInvestorStatement({
        id: firmId,
        investorId: selectedInvestor,
        year,
        month
      })).unwrap();
      if (response && response.data && Array.isArray(response.data)) {
        // Map API data by Frequency
        const freqMap = {};
        response.data.forEach(item => {
          freqMap[item.Frequency] = item;
        });
        setStatementData({
          investor: investors.find(inv => inv.InvestorID === selectedInvestor),
          date: selectedDate,
          data: [
            {
              description: "Beginning Capital ($)",
              mtd: freqMap.PTD?.BeginningCapital ?? '-',
              qtd: freqMap.QTD?.BeginningCapital ?? '-',
              ytd: freqMap.YTD?.BeginningCapital ?? '-',
            },
            {
              description: "Additions($)",
              mtd: freqMap.PTD?.BeginningAdditions ?? '-',
              qtd: freqMap.QTD?.BeginningAdditions ?? '-',
              ytd: freqMap.YTD?.BeginningAdditions ?? '-',
            },
            {
              description: "Redemptions($)",
              mtd: freqMap.PTD?.BeginningRedemptions ?? '-',
              qtd: freqMap.QTD?.BeginningRedemptions ?? '-',
              ytd: freqMap.YTD?.BeginningRedemptions ?? '-',
            },
            {
              description: "Transfers($)",
              mtd: freqMap.PTD?.BeginningTransfers ?? '-',
              qtd: freqMap.QTD?.BeginningTransfers ?? '-',
              ytd: freqMap.YTD?.BeginningTransfers ?? '-',
            },
            {
              description: "Management Fee($)",
              mtd: freqMap.PTD?.ManagementFee ?? '-',
              qtd: freqMap.QTD?.ManagementFee ?? '-',
              ytd: freqMap.YTD?.ManagementFee ?? '-',
            },
            {
              description: "Incentive Fee($)",
              mtd: freqMap.PTD?.IncentiveFee ?? '-',
              qtd: freqMap.QTD?.IncentiveFee ?? '-',
              ytd: freqMap.YTD?.IncentiveFee ?? '-',
            },
            {
              description: "Net Income($)",
              mtd: freqMap.PTD?.NetIncome ?? '-',
              qtd: freqMap.QTD?.NetIncome ?? '-',
              ytd: freqMap.YTD?.NetIncome ?? '-',
            },
            {
              description: "Ending Additions($)",
              mtd: freqMap.PTD?.EndingAdditions ?? '-',
              qtd: freqMap.QTD?.EndingAdditions ?? '-',
              ytd: freqMap.YTD?.EndingAdditions ?? '-',
            },
            {
              description: "Ending Redemptions($)",
              mtd: freqMap.PTD?.EndingRedemptions ?? '-',
              qtd: freqMap.QTD?.EndingRedemptions ?? '-',
              ytd: freqMap.YTD?.EndingRedemptions ?? '-',
            },
            {
              description: "Ending Transfers($)",
              mtd: freqMap.PTD?.EndingTransfers ?? '-',
              qtd: freqMap.QTD?.EndingTransfers ?? '-',
              ytd: freqMap.YTD?.EndingTransfers ?? '-',
            },
            {
              description: "Ending Capital($)",
              mtd: freqMap.PTD?.EndingCapital ?? '-',
              qtd: freqMap.QTD?.EndingCapital ?? '-',
              ytd: freqMap.YTD?.EndingCapital ?? '-',
            },
            {
              description: "ROR",
              mtd: freqMap.PTD?.ROR ?? '-',
              qtd: freqMap.QTD?.ROR ?? '-',
              ytd: freqMap.YTD?.ROR ?? '-',
            },
            {
              description: "Ending Unit",
              mtd: freqMap.PTD?.EndingUnit ?? '-',
              qtd: freqMap.QTD?.EndingUnit ?? '-',
              ytd: freqMap.YTD?.EndingUnit ?? '-',
            },
          ]
        });
      } else {
        setStatementData(null);
      }
    } catch (error) {
      setStatementData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="investor-statements-container" sx={{ p: 2, bgcolor: '#fafbfc', borderRadius: 2 }}>
      {/* Controls section */}
      <Box className="controls-container" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {/* Investor Name dropdown - single select */}
        <TextField
          select
          label="Investor Name"
          value={selectedInvestor}
          onChange={e => { setSelectedInvestor(e.target.value); setStatementData(null); }}
          sx={{ width: 440, background: '#fff' }}
          size="small"
          variant="outlined"
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: {
                  maxHeight: 300,
                  maxWidth: 300,
                },
              },
            },
          }}
        >
          {uniqueInvestors.map(inv => (
            <MenuItem key={inv.InvestorID} value={inv.InvestorID}>
              {inv.InvestorName} ({inv.InvestorNumber.split('_')[0]})
            </MenuItem>
          ))}
        </TextField>
        {/* Date selection area */}
        <Box className="date-selection-container" ref={dropdownRef} sx={{ width: 140 }}>
          <TextField
            label="MM/YYYY"
            value={selectedDate}
            onClick={handleDateClick}
            readOnly
            sx={{ width: 190, background: '#fff' }}
            size="small"
            variant="outlined"
          />
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleDateClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", p: 1, minWidth: 180 }}>
              <IconButton onClick={() => changeYear('prev')} disabled={currentYear === availableYears[0]}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ mx: 2, fontWeight: "bold" }}>{currentYear}</Typography>
              <IconButton onClick={() => changeYear('next')} disabled={currentYear === availableYears[availableYears.length - 1]}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            {months.map((month, idx) => {
              const monthNum = (idx + 1).toString().padStart(2, '0');
              const value = `${monthNum}/${currentYear}`;
              return (
                <MenuItem
                  key={month}
                  selected={selectedDate === value}
                  onClick={() => handleMonthSelect(monthNum)}
                  sx={{ pl: 4, pr: 2 }}
                >
                  {selectedDate === value && <CheckIcon fontSize="small" sx={{ mr: 1 }} />}
                  {month}
                </MenuItem>
              );
            })}
          </Popover>
        </Box>
        {/* Generate button */}
        <Button variant="contained" onClick={generateStatement} disabled={!selectedInvestor || !selectedDate} sx={{ height: 40, minWidth: 170, fontWeight: 500, fontSize: 15, boxShadow: 'none', borderRadius: 1 }}>Generate Statement</Button>
      </Box>
      {/* Results section or info state */}
      {loading && (
        <Box sx={{ mt: 2, p: 4, bgcolor: '#fff', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, border: '1px solid #e0e0e0' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}
      {!loading && !statementData && (
        <Box sx={{ mt: 2, p: 4, bgcolor: '#fff', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, border: '1px solid #e0e0e0' }}>
          <InfoOutlinedIcon sx={{ fontSize: 48, color: '#1a237e', mb: 1 }} />
          <Typography sx={{ color: '#1a237e', fontWeight: 500, mb: 0.5 }}>No investor selected yet.</Typography>
          <Typography sx={{ color: '#666', fontSize: 15 }}>Please select an Investor to view statement.</Typography>
        </Box>
      )}
      {statementData && (
        <Box className="statement-results" sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Statement for {statementData.investor?.InvestorName} ({statementData.investor?.InvestorNumber}) - {statementData.date}
          </Typography>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">MTD</TableCell>
                  <TableCell align="right">QTD</TableCell>
                  <TableCell align="right">YTD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statementData.data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">{row.description}</TableCell>
                    <TableCell align="right">{row.mtd}</TableCell>
                    <TableCell align="right">{row.qtd}</TableCell>
                    <TableCell align="right">{row.ytd}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default InvestorStatementsTab;